import React from 'react'
import { useTranslation } from 'react-i18next'
import { FormControl, Box, Typography, FormHelperText } from '@mui/material'
import { useFormikContext } from 'formik'
import theme from 'lib/theme'
import { contentLanguages } from 'lib/languages'
import { SmallDropdown } from 'components/SmallDropdown/SmallDropdown'
import { StyledInput, StyledLabel } from './InputWithDropdown.styled'

type InputWithDropdownProps = {
  multiline?: boolean
  label: string
  value: string
  selectedOption: string
  dropdownFieldName: string
  inputFieldName: string
  inputOptions: { [lang: string]: string }
  helperText?: string
  handleChange?: (options: { [lang: string]: string }) => void
  handleLanguageChange?: (option: string) => void
  handleBlur?: () => void
}

export const InputWithDropdown = ({
  multiline,
  label,
  value,
  selectedOption,
  dropdownFieldName,
  inputFieldName,
  inputOptions,
  helperText,
  handleChange: handleInputChange,
  handleLanguageChange,
  handleBlur,
}: InputWithDropdownProps) => {
  const { setFieldValue } = useFormikContext()
  const { t } = useTranslation()

  const handleChange = (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    // new reference required for formik to detect if dirty
    const newOptions = { ...inputOptions }
    newOptions[selectedOption] = event.target.value
    if (handleInputChange) {
      handleInputChange(newOptions)
    } else {
      setFieldValue(inputFieldName, newOptions)
    }
  }

  return (
    <FormControl>
      <StyledLabel>{label}</StyledLabel>
      {contentLanguages?.length > 1 && (
        <Box
          display='flex'
          sx={{ position: 'absolute', right: 0, top: '-16px' }}
          data-testid={`${label} dropdown`}>
          {contentLanguages.map((lang) => (
            <Typography
              key={lang + label}
              sx={{
                fontSize: '10px',
                fontWeight: 'bold',
                color:
                  inputOptions[lang].length > 0
                    ? theme.palette.text.secondary
                    : theme.palette.neutral.grey7,
              }}
              mr={0.5}>
              {lang.toUpperCase()}
            </Typography>
          ))}
        </Box>
      )}
      <StyledInput
        inputProps={{ 'data-testid': `${label} input` }}
        posAbs={Boolean(multiline && contentLanguages?.length > 1)}
        multiline={multiline}
        onChange={handleChange}
        value={value}
        type='text'
        onBlur={() => {
          if (handleBlur) {
            handleBlur()
          }
        }}
        startAdornment={
          contentLanguages?.length > 1 && (
            <SmallDropdown
              selectedOption={selectedOption}
              options={contentLanguages}
              fieldName={dropdownFieldName}
              handleChange={handleLanguageChange}
              ariaLabel={t('selectLanguageFor', {
                field: label,
              })}
            />
          )
        }
      />
      <FormHelperText sx={{ marginLeft: theme.spacing(1) }}>
        {helperText || ''}
      </FormHelperText>
    </FormControl>
  )
}
